import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CMSData, PageContent } from './types';
import { afterBlockingWindow } from '../lib/after-blocking-window';
// DEV-12091: only the eager SUBSET (shell + homepage) ships in the entry chunk.
// The full English CMS data (all other pages + the ~230 KB of API-docs code
// samples) and the full fallback are imported LAZILY via import() below, so the
// homepage no longer waits on `/developers` content to hydrate.
import { fallbackEager } from './fallback.eager';
import enEager from './data.en.eager';

export type {
  CMSData,
  PageContent,
  Product,
  UseCase,
  PricingPlan,
  NavItem,
  FooterColumn,
  FAQ,
  BlogPost,
} from './types';

const SUPPORTED_LOCALES = ['en', 'es', 'de', 'fr', 'zh'] as const;
type Locale = (typeof SUPPORTED_LOCALES)[number];

function isLocale(l: string | undefined): l is Locale {
  return !!l && (SUPPORTED_LOCALES as readonly string[]).includes(l);
}

// Deep-merge: generated CMS data wins per-key, fallback fills any gaps.
function mergePages(
  fallback: Record<string, PageContent>,
  gen: Record<string, PageContent> | undefined,
): Record<string, PageContent> {
  if (!gen) return fallback;
  const merged: Record<string, PageContent> = {};
  const allSlugs = new Set([...Object.keys(fallback), ...Object.keys(gen)]);
  for (const slug of allSlugs) {
    merged[slug] = { ...fallback[slug], ...gen[slug] };
  }
  return merged;
}

// Rename legacy labels so Notion data matches the canonical names used in code.
const RENAMES: Record<string, string> = { 'Custom Collections': 'LunarCrush Collections' };

function applyRenames<T extends { name?: string; label?: string }>(arr: T[], key: 'name' | 'label'): T[] {
  return arr.map((item) => {
    const val = item[key] as string | undefined;
    return val && RENAMES[val] ? { ...item, [key]: RENAMES[val] } : item;
  });
}

// Ensure code-defined entries (not yet in CMS) are present in generated arrays.
function injectIfMissing<T extends { name?: string; label?: string }>(
  arr: T[],
  fallbackArr: T[],
  names: string[],
  key: 'name' | 'label',
): T[] {
  const renamed = applyRenames(arr, key);
  const existing = new Set(renamed.map((item) => item[key]));
  const missing = fallbackArr.filter((item) => names.includes(item[key] as string) && !existing.has(item[key]));
  return missing.length ? [...renamed, ...missing] : renamed;
}

// Apply every legacy normalization layer to one locale's CMS data and return
// the shape consumers expect. Pure: same (fallback, generated) → same output.
// `fallback` is passed in (not imported) so the eager path can process the small
// shell subset while the full path processes the complete fallback.
function processLocale(fallback: CMSData, generated: CMSData | null | undefined): CMSData {
  const mergedPages = mergePages(fallback.pages, generated?.pages);

  // Homepage inherits shared fields. Other pages read their own fields directly.
  if (mergedPages.shared && mergedPages.homepage) {
    mergedPages.homepage = { ...mergedPages.shared, ...mergedPages.homepage };
  }

  // Inject CLI into hero_build_platforms if missing from generated homepage data.
  if (mergedPages.homepage?.hero_build_platforms) {
    try {
      const platforms = JSON.parse(mergedPages.homepage.hero_build_platforms);
      if (Array.isArray(platforms) && !platforms.some((p: { name: string }) => p.name === 'LunarCrush CLI')) {
        const fbPlatforms = JSON.parse(fallback.pages.homepage?.hero_build_platforms || '[]');
        const cliEntry = fbPlatforms.find((p: { name: string }) => p.name === 'LunarCrush CLI');
        if (cliEntry) {
          platforms.push(cliEntry);
          mergedPages.homepage = { ...mergedPages.homepage, hero_build_platforms: JSON.stringify(platforms) };
        }
      }
    } catch { /* ignore parse errors */ }
  }

  return {
    pages: mergedPages,
    products: injectIfMissing(
      generated?.products?.length ? generated.products : fallback.products,
      fallback.products,
      ['LunarCrush CLI', 'LunarCrush Collections'],
      'name',
    ),
    useCases: generated?.useCases?.length ? generated.useCases : fallback.useCases,
    pricingPlans: generated?.pricingPlans?.length ? generated.pricingPlans : fallback.pricingPlans,
    navigation: injectIfMissing(
      generated?.navigation?.length ? generated.navigation : fallback.navigation,
      fallback.navigation,
      ['LunarCrush CLI', 'LunarCrush Collections'],
      'label',
    ),
    footerColumns: (generated?.footerColumns?.length ? generated.footerColumns : fallback.footerColumns).map(
      (col) => {
        if (col.title === 'Platform' || col.title === 'Product') {
          // Rename legacy labels and deduplicate.
          const links = col.links
            .map((l) => RENAMES[l] || l)
            .filter((l, i, a) => a.indexOf(l) === i);
          if (!links.includes('LunarCrush CLI')) links.push('LunarCrush CLI');
          if (!links.includes('LunarCrush Collections')) links.push('LunarCrush Collections');
          return { ...col, links };
        }
        return col;
      },
    ),
    footerSocialLinks: generated?.footerSocialLinks?.length ? generated.footerSocialLinks : fallback.footerSocialLinks,
    faqs: generated?.faqs?.length ? generated.faqs : fallback.faqs,
    blogPosts: generated?.blogPosts?.length ? generated.blogPosts : fallback.blogPosts,
  };
}

// ── Eager seed (entry chunk) ─────────────────────────────────
// Home + shell + pricing content, processed synchronously at module init.
// Every hook returns this until the matching locale's FULL data has loaded.
// Because the eager and full English data carry byte-identical homepage/shell
// (and pricing) content, those routes never flash when they upgrade eager → full.
const EN_EAGER = processLocale(fallbackEager, enEager);

// ── Full data (lazy chunks) ──────────────────────────────────
// Each locale's full CMSData is imported on demand. Vite emits data.<locale>.ts
// and fallback.ts as their own chunks, so none of them weigh down the entry.
const FULL_LOADERS: Record<Locale, () => Promise<{ default: CMSData | null }>> = {
  en: () => import('./data.en'),
  es: () => import('./data.es'),
  de: () => import('./data.de'),
  fr: () => import('./data.fr'),
  zh: () => import('./data.zh'),
};

// Processed FULL data cache. Starts empty — the eager seed covers first paint.
const PROCESSED_FULL: Partial<Record<Locale, CMSData>> = {};
const FULL_READY = new Set<Locale>();
const FULL_PROMISES: Partial<Record<Locale, Promise<void>>> = {};
const SUBSCRIBERS = new Set<() => void>();

async function computeFull(loc: Locale): Promise<CMSData> {
  const [fbMod, genMod] = await Promise.all([import('./fallback'), FULL_LOADERS[loc]()]);
  if (genMod.default) return processLocale(fbMod.fallbackData, genMod.default);
  // A locale chunk with no content (untranslated) falls back to full English.
  if (loc === 'en') return processLocale(fbMod.fallbackData, null);
  return computeFull('en');
}

// Load a locale's FULL data once, cache it, and notify mounted consumers so they
// re-render with the now-complete data. Returns a promise that resolves when the
// data is ready (used by useEnsureFullCMS to suspend route content).
function ensureFull(loc: Locale): Promise<void> {
  if (FULL_READY.has(loc)) return Promise.resolve();
  let pr = FULL_PROMISES[loc];
  if (!pr) {
    pr = computeFull(loc)
      .then((data) => { PROCESSED_FULL[loc] = data; })
      .catch(() => { /* network/parse failure — keep serving the eager seed */ })
      .finally(() => {
        FULL_READY.add(loc);
        for (const notify of SUBSCRIBERS) notify();
      });
    FULL_PROMISES[loc] = pr;
  }
  return pr;
}

// Schedule the full-data prefetch off the critical path so it never competes
// with LCP work or lands a parse task inside the TBT window.
//
// DEV-12444: do NOT use requestIdleCallback. It fires the moment the thread is
// idle — still inside the 3.5s bench settle — and the full English CMS is
// ~750 KB of source. Same lesson as the GA deferral in index.html / DEV-12330.
function prefetchFull(loc: Locale): void {
  if (FULL_READY.has(loc) || FULL_PROMISES[loc]) return;
  afterBlockingWindow(() => { void ensureFull(loc); });
}

/** Empty page used as safe fallback when a page slug is missing. */
const EMPTY_PAGE: PageContent = {};

// ── Locale-aware hooks ───────────────────────────────────────
/** Reactive CMS data for the active locale. Returns the eager shell+homepage
 *  seed immediately, then re-renders with the full data once it has loaded
 *  (prefetched at idle). Home + shell content is identical across the two, so
 *  the homepage does not flash on upgrade. */
export function useCMS(): CMSData {
  const { i18n } = useTranslation();
  const lng = i18n.resolvedLanguage;
  const loc: Locale = isLocale(lng) ? lng : 'en';

  const [, forceRender] = useState(0);
  useEffect(() => {
    if (FULL_READY.has(loc)) return;
    prefetchFull(loc);
    const onLoaded = () => forceRender((n) => n + 1);
    SUBSCRIBERS.add(onLoaded);
    return () => { SUBSCRIBERS.delete(onLoaded); };
  }, [loc]);

  // Full data for the active locale if loaded; otherwise the English eager seed
  // (shell + homepage), matching the pre-DEV-10949 "English until the locale
  // chunk resolves" behaviour.
  return PROCESSED_FULL[loc] ?? EN_EAGER;
}

/** Suspends the calling component until the active locale's FULL CMS data has
 *  loaded. Used to gate non-home routes so their (previously eager-absent) page
 *  content is never rendered empty — it appears together with the route chunk,
 *  inside the same Suspense boundary, so there is no content flash. */
export function useEnsureFullCMS(): void {
  const { i18n } = useTranslation();
  const lng = i18n.resolvedLanguage;
  const loc: Locale = isLocale(lng) ? lng : 'en';
  if (!FULL_READY.has(loc)) throw ensureFull(loc);
}

export function useCMSPage(slug: string): PageContent {
  return useCMS().pages[slug] ?? EMPTY_PAGE;
}

export function useFAQBySlug(slug: string) {
  return useCMS().faqs.find((f) => f.slug === slug);
}

export function useFAQsByCategory(category: string) {
  return useCMS().faqs.filter((f) => f.category === category).sort((a, b) => a.order - b.order);
}

export function useFAQCategories(): string[] {
  const seen = new Set<string>();
  const categories: string[] = [];
  for (const faq of useCMS().faqs) {
    if (!seen.has(faq.category)) {
      seen.add(faq.category);
      categories.push(faq.category);
    }
  }
  return categories;
}

export function useBlogPostBySlug(slug: string) {
  return useCMS().blogPosts.find((p) => p.slug === slug);
}

export function useBlogPostsByCategory(category: string) {
  return useCMS().blogPosts.filter((p) => p.category === category);
}

export function useBlogCategories(): string[] {
  const seen = new Set<string>();
  const categories: string[] = [];
  for (const post of useCMS().blogPosts) {
    if (!seen.has(post.category)) {
      seen.add(post.category);
      categories.push(post.category);
    }
  }
  return categories;
}

export function useFeaturedBlogPosts() {
  return useCMS().blogPosts.filter((p) => p.featured);
}
